"use strict";
const BrowserDetect = require("@gluenetworks/srvc_core_browser-detect");
const _ = require("lodash");

class InputNumberControlRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("input-number-control");
        this._isEdge = BrowserDetect.browser === "Edge";
        this._textField = document.createElement("input");
        this._textField.setAttribute("autocomplete", "off");
        this._spinner = document.createElement("div");
        this._spinner.classList.add("spinner");
        this._spinnerIncrement = document.createElement("a");
        this._spinnerIncrement.classList.add("increment");
        this._spinnerDecrement = document.createElement("a");
        this._spinnerDecrement.classList.add("decrement");
        this._spinner.appendChild(this._spinnerIncrement);
        this._spinner.appendChild(this._spinnerDecrement);
        if (this._isEdge) {
            this._textField.setAttribute("type", "text");
            this._rootElem.appendChild(this._spinner);
        } else {
            this._textField.setAttribute("type", "number");
        }
        this._errorSpan = document.createElement("span");
        this._errorSpan.classList.add("error");
        this._rootElem.appendChild(this._textField);
        this._rootElem.appendChild(this._errorSpan);
        this._rootElem.component = this._model;
        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown() {
        let prom = new Promise((resolve /*,reject*/) => {
            this._unregisterListeners();
            this._model = undefined;
            this._rootElem.remove();
            this._textField = undefined;
            this._spinner = undefined;
            this._spinnerIncrement = undefined;
            this._spinnerDecrement = undefined;
            this._rootElem = undefined;
            resolve();
        });
        return prom;
    }
    _registerListeners() {
        this._model.on("Disabled", (this._onDisabled = this._onDisabled.bind(this)));
        this._model.on("Value", (this._onValue = this._onValue.bind(this)));
        this._model.on("Visible", (this._onVisible = this._onVisible.bind(this)));
        this._model.on("OnlyInteger", (this._onOnlyInteger = this._onOnlyInteger.bind(this)));
        this._model.on("Invalid", (this._onInvalid = this._onInvalid.bind(this)));
        this._model.on("Placeholder", (this._onPlaceholder = this._onPlaceholder.bind(this)));
        this._model.on("Min", (this._onMin = this._onMin.bind(this)));
        this._model.on("Max", (this._onMax = this._onMax.bind(this)));
        this._model.on("Step", (this._onStep = this._onStep.bind(this)));
        this._model.on("DarkTheme", (this._onDarkTheme = this._onDarkTheme.bind(this)));
        this._model.on("Theme", (this._onTheme = this._onTheme.bind(this)));
        this._model.on("Focus", (this._onFocus = this._onFocus.bind(this)));
        this._model.on("Blur", (this._onBlur = this._onBlur.bind(this)));
        this._model.on("Required", (this._onRequired = this._onRequired.bind(this)));
        this._model.on("SetAttribute", (this._onSetAttribute = this._onSetAttribute.bind(this)));
        this._model.on("Tooltip", (this._onTooltip = this._onTooltip.bind(this)));
        this._textField.addEventListener(
            "input",
            (this._onInputEvent = this._onInputEvent.bind(this))
        );
        this._textField.addEventListener(
            "focus",
            (this._onFocusEvent = this._onFocusEvent.bind(this))
        );
        this._textField.addEventListener(
            "blur",
            (this._onBlurEvent = this._onBlurEvent.bind(this))
        );
        this._textField.addEventListener(
            "keydown",
            (this._onKeyDownEvent = this._onKeyDownEvent.bind(this))
        );
        this._textField.addEventListener(
            "keypress",
            (this._onKeyPressEvent = this._onKeyPressEvent.bind(this))
        );
        this._spinnerIncrement.addEventListener(
            "mousedown",
            (this._onSpinnerIncrementMouseDownEvent =
                this._onSpinnerIncrementMouseDownEvent.bind(this))
        );
        this._spinnerDecrement.addEventListener(
            "mousedown",
            (this._onSpinnerDecrementMouseDownEvent =
                this._onSpinnerDecrementMouseDownEvent.bind(this))
        );
    }
    _unregisterListeners() {
        this._model.removeListener("Disabled", this._onDisabled);
        this._model.removeListener("Value", this._onValue);
        this._model.removeListener("Visible", this._onVisible);
        this._model.removeListener("Invalid", this._onInvalid);
        this._model.removeListener("Placeholder", this._onPlaceholder);
        this._model.removeListener("Min", this._onMin);
        this._model.removeListener("Max", this._onMax);
        this._model.removeListener("Step", this._onStep);
        this._model.removeListener("DarkTheme", this._onDarkTheme);
        this._model.removeListener("Theme", this._onTheme);
        this._model.removeListener("Focus", this._onFocus);
        this._model.removeListener("Blur", this._onBlur);
        this._model.removeListener("Required", this._onRequired);
        this._model.removeListener("SetAttribute", this._onSetAttribute);
        this._model.removeListener("OnlyInteger", this._onOnlyInteger);
        this._model.removeListener("Tooltip", this._onTooltip);
        this._textField.removeEventListener("input", this._onInputEvent);
        this._textField.removeEventListener("focus", this._onFocusEvent);
        this._textField.removeEventListener("blur", this._onBlurEvent);
        this._textField.removeEventListener("keydown", this._onKeyDownEvent);
        this._textField.removeEventListener("keypress", this._onKeyPressEvent);
        this._spinnerIncrement.addEventListener(
            "mousedown",
            this._onSpinnerIncrementMouseDownEvent
        );
        this._spinnerDecrement.addEventListener(
            "mousedown",
            this._onSpinnerDecrementMouseDownEvent
        );
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render() {
        let prom = new Promise((resolve /*,reject*/) => {
            let isInvalid = this._model.invalid;
            this._setDisabled(this._model.disabled);
            this._setValue(this._model.value);
            this._setVisible(this._model.visible);
            this._parseUserEntry();
            this._setPlaceholder(this._model.placeholder);
            this._setMin(this._model.min);
            this._setMax(this._model.max);
            this._setStep(this._model.step);
            this._setRequired(this._model.required);
            this._setInvalid(isInvalid || !this._isValidNumber());
            this._setDarkTheme(this._model.darkTheme);
            this._setTheme(this._model.theme);
            this._setTooltip(this._model.tooltip);
            resolve();
        });
        return prom;
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the disabled state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDisabled(newVal) {
        this._setDisabled(newVal);
    }

    /**
     * Fired when the onlyInteger value has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onOnlyInteger(newVal) {
        this._setOnlyInteger(newVal);
    }

    /**
     * Fired when the invalid state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onInvalid(newVal) {
        this._setInvalid(newVal || !this._isValidNumber());
    }

    /**
     * Fired when the value has been updated in the model
     * @param newVal {Number}
     * @param oldVal {Number}
     * @private
     */
    _onValue(newVal /*, oldVal*/) {
        this._setValue(newVal);
    }

    /**
     * Fired when the visible state has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onVisible(newVal) {
        this._setVisible(newVal);
    }

    /**
     * Fired when the placeholder has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onPlaceholder(newVal) {
        this._setPlaceholder(newVal);
    }

    /**
     * Fired when the min value has been updated in the model
     * @param newVal {Number}
     * @private
     */
    _onMin(newVal) {
        this._setMin(newVal);
    }

    /**
     * Fired when the min value has been updated in the model
     * @param newVal {Number}
     * @private
     */
    _onMax(newVal) {
        this._setMax(newVal);
    }

    /**
     * Fired when the step value has been updated in the model
     * @param newVal {Number|String}
     * @private
     */
    _onStep(newVal) {
        this._setStep(newVal);
    }

    /**
     * Fired when the dark theme has been updated in the model
     * @param newVal {Boolean}
     * @private
     */
    _onDarkTheme(newVal) {
        this._setDarkTheme(newVal);
    }

    /**
     * Fired when the theme has been updated in the model
     * @param newVal {String}
     * @private
     */
    _onTheme(newVal, oldVal) {
        this._setTheme(newVal, oldVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus() {
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur() {
        this._setFocus(false);
    }

    /**
     * Fired when required property is updated in the model
     * @param newRequiredVal
     * @private
     */
    _onRequired(newRequiredVal) {
        this._setRequired(newRequiredVal);
    }

    /**
     * Fired when setAttribute has been called in the model
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _onSetAttribute(attrName, attrValue) {
        this._setAttribute(attrName, attrValue);
    }
    /**
     * Fired when tooltip/title has been changed in the model
     * @param newVal {String} - The tooltip/title of the HTML attribute to be set on the native control
     * @private
     */
    _onTooltip(newVal) {
        this._setTooltip(newVal);
    }

    /**
     * Parse what was entered into a number and update the model
     * @param event {Event}
     * @private
     */
    _onInputEvent(event) {
        let oldVal = _.cloneDeep(this._model.value);
        this._parseUserEntry();
        this._model._triggerUserValue(this._model.value, oldVal, event);
        let invalid = !this._isValidNumber();
        this._setInvalid(invalid);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(event) {
        this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(event) {
        this._model._triggerUserBlur(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onKeyDownEvent(event) {
        this._model._triggerUserKeyDown(event);
    }

    /**
     * If Edge, check that the key is a valid entry for a number
     * @param event {Event}
     * @private
     */
    _onKeyPressEvent(event) {
        if (
            this._isEdge &&
            event.key.match(/[0-9]/) === null &&
            event.key !== "-" &&
            event.key !== "."
        ) {
            event.preventDefault();
            return;
        }
    }

    /**
     * Increment models value when the 'Increment' spinner is clicked
     * @param event {Event}
     * @private
     */
    _onSpinnerIncrementMouseDownEvent(/*event*/) {
        if (!this._model._disabled) {
            this._incrementValue();
        }
    }

    /**
     * Decrement models value when the 'Decrement' spinner is clicked
     * @param event {Event}
     * @private
     */
    _onSpinnerDecrementMouseDownEvent(/*event*/) {
        if (!this._model._disabled) {
            this._decrementValue();
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Toggle the disabled state
     * @param newVal {Boolean}
     * @private
     */
    _setDisabled(newVal) {
        if (newVal === true) {
            this._textField.disabled = true;
            this._rootElem.classList.add("disabled");
        } else {
            this._textField.disabled = false;
            this._rootElem.classList.remove("disabled");
        }
    }

    /**
     * Set onlyInteger value
     * @param newVal {Boolean}
     * @private
     */
    _setOnlyInteger(/**newVal*/) {
        this._setInvalid(!this._isValidNumber());
    }

    /**
     * Toggle the invalid state
     * @param newVal {Boolean}
     * @private
     */
    _setInvalid(newVal) {
        let oldVal = this._model._invalid;
        if (newVal === true) {
            this._model._invalid = true;
            this._rootElem.classList.add("invalid");
            this._model._validationMessages = this._getViolations();
            this._errorSpan.textContent = this._model._validationMessages;
        } else {
            this._model._invalid = false;
            this._model._validationMessages = [];
            this._rootElem.classList.remove("invalid");
        }
        if (newVal !== oldVal) {
            this._model._triggerInvalidStatusEvent(newVal, oldVal);
        }
    }

    /**
     * Put the placeholder text in place and move the cursor to the front of the selection if placeholder text
     * is displaying.
     * @param newVal {String}
     * @private
     */
    _setPlaceholder(newVal) {
        if (newVal !== undefined && newVal !== null && newVal !== "") {
            this._textField.placeholder = newVal;
        } else {
            this._textField.placeholder = "";
        }
    }

    /**
     * Toggle the display text value of the button
     * @param newVal {String|Number}
     * @private
     */
    _setValue(newVal) {
        let currentVal = parseFloat(this._textField.value);
        if (newVal !== currentVal) {
            if (newVal || String(newVal) === "0") {
                this._textField.value = newVal;
            } else {
                this._textField.value = null;
            }
        }
    }

    /**
     * Toggle the visible state
     * @param newVal {Boolean}
     * @private
     */
    _setVisible(newVal) {
        if (newVal === true) {
            this._rootElem.classList.remove("hidden");
        } else {
            this._rootElem.classList.add("hidden");
        }
    }

    /**
     * Toggle or set the min attribute
     * @param newVal {Number}
     * @private
     */
    _setMin(newVal) {
        if (newVal !== undefined) {
            this._textField.min = newVal;
        } else {
            this._textField.min = "";
        }
        this._setInvalid(!this._isValidNumber());
    }

    /**
     * Toggle or set the max attribute
     * @param newVal {Number}
     * @private
     */
    _setMax(newVal) {
        if (newVal !== undefined) {
            this._textField.max = newVal;
        } else {
            this._textField.max = "";
        }
        this._setInvalid(!this._isValidNumber());
    }

    /**
     * Toggle or set the step/step attribute
     * @param newVal {Number|string}
     * @private
     */
    _setStep(newVal) {
        if (newVal) {
            this._textField.step = newVal;
        } else {
            this._textField.removeAttribute("step");
        }
        this._setInvalid(!this._isValidNumber());
    }

    /**
     * Toggle or set the dark theme
     * @param newVal {Boolean}
     * @private
     */
    _setDarkTheme(newVal) {
        if (newVal) {
            this._rootElem.classList.add("dark");
        } else {
            this._rootElem.classList.remove("dark");
        }
    }

    /**
     * Set the theme name
     * @param newVal {String}
     * @private
     */
    _setTheme(newVal, oldVal) {
        if (oldVal) {
            this._rootElem.classList.remove(oldVal);
        }
        if (newVal) {
            this._rootElem.classList.add(newVal);
        }
    }

    /**
     * Set or remove focus on the textfield
     * @param newVal {Boolean}
     * @private
     */
    _setFocus(newVal) {
        if (newVal === true) {
            this._textField.focus();
        } else {
            this._textField.blur();
        }
    }

    /**
     * Parse the users interaction and update the value in the model
     * @params newVal - new raw value of input control
     * @private
     */
    _parseUserEntry() {
        //TODO: Revisit in validation effort. Values like 0000 are treated as 0 because of the parse.
        let val = parseFloat(this._textField.value),
            trailingDecimal =
                this._textField.value.charAt(this._textField.value.length - 1) === ".",
            trailingZero = this._textField.value.charAt(this._textField.value.length - 1) === "0",
            containsDecimal = this._textField.value.indexOf(".") !== -1;
        if (this._textField.value.length === 0 || isNaN(val)) {
            this._model.setValue(null, true);
        } else {
            this._model.setValue(val, true);
        }
        if (this._isEdge && !isNaN(val)) {
            if (trailingDecimal) {
                this._textField.value = this._model._value + ".";
            } else if (trailingZero || (containsDecimal && trailingZero)) {
                this._textField.value = this._textField.value;
            } else {
                this._textField.value = this._model._value;
            }
        }
    }

    /**
     * Increment models value based on user interaction
     * @private
     */
    _incrementValue() {
        let val = isNaN(this._model.value) ? undefined : Math.floor(this._model.value);
        if (!_.isUndefined(val)) {
            val = val === 0 ? 1 : val + 1;
            if (this._model.max) {
                val = val > this._model.max ? this._model.max : val;
            }
            this._model.setValue(val, true);
            this._textField.value = this._model.value;
        }
    }

    /**
     * Decrement models value based on user interaction
     * @private
     */
    _decrementValue() {
        let val = isNaN(this._model.value) ? undefined : Math.floor(this._model.value);
        if (!_.isUndefined(val)) {
            val = val === 0 ? -1 : val - 1;
            if (this._model.min) {
                val = val < this._model.min ? this._model.min : val;
            }
            this._model.setValue(val, true);
            this._textField.value = this._model.value;
        }
    }

    /**
     * Set an attribute on the native control
     * @param attrName {String} - The name of the HTML attribute to be set on the native control
     * @param attrValue {String|Undefined} - The value of the HTML attribute to be set on the native control
     * @private
     */
    _setAttribute(attrName, attrValue) {
        if (!_.isUndefined(attrValue)) {
            this._textField.setAttribute(attrName, attrValue);
        } else {
            this._textField.removeAttribute(attrName);
        }
    }

    /**
     * Updates required attribute on the control
     * @param newRequiredVal
     * @private
     */
    _setRequired(newRequiredVal) {
        if (newRequiredVal === true) {
            this._textField.setAttribute("required", "");
        } else {
            this._textField.removeAttribute("required");
        }
        this._setInvalid(!this._isValidNumber());
    }

    /**
     * Set tooltip/title on the native control
     * @param newVal {String} - The tooltip/title of the HTML attribute to be set on the native control
     * @private
     */
    _setTooltip(newVal) {
        if (newVal) {
            this._textField.title = newVal;
        } else {
            this._textField.removeAttribute("title");
        }
    }

    _getViolations() {
        let validationMessages = [];
        let inputValue = this._model.value;
        if (this._textField.validity.valueMissing || this._textField.validity.badInput) {
            let validationMessage = this._model.onlyInteger
                ? "Please enter an integer"
                : "Please enter a number";
            validationMessages.push(validationMessage);
        } else if (!_.isNil(inputValue)) {
            if (this._model.onlyInteger && !_.isInteger(inputValue)) {
                validationMessages.push("Please enter an integer");
            } else if (this._textField.validity.rangeUnderflow) {
                validationMessages.push(
                    "Please enter a value greater than or equal to " + this._model.min
                );
            } else if (this._textField.validity.rangeOverflow) {
                validationMessages.push(
                    "Please enter a value less than or equal to  " + this._model.max
                );
            } else if (this._textField.validity.stepMismatch) {
                validationMessages.push(this._textField.validationMessage);
            }
        }
        return validationMessages;
    }

    _isValidNumber() {
        if (!this._textField.validity.valid) {
            return false;
        } else {
            if (!this._model.value) {
                return true;
            } else if (this.model.onlyInteger) {
                return _.isInteger(this.model.value);
            } else {
                return true;
            }
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = InputNumberControlRenderer;
